import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { useAppStore } from '../store/useAppStore';

const API_URL = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = useAppStore.getState().token;
        
        // Add auth token
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add Telegram initData if available
        const initData = window.Telegram?.WebApp?.initData;
        if (initData) {
          config.headers['X-Telegram-Init-Data'] = initData;
        }

        // Debug logging for auth-related requests
        console.log('[API Request]', {
          url: config.url,
          method: config.method,
          hasToken: !!token,
          tokenPreview: token ? `${token.substring(0, 20)}...` : 'none',
          hasInitData: !!initData,
          initDataLength: initData?.length || 0,
        });

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Only logout on 401 if we're NOT in dev mode with a mock token
        // This prevents clearing user state when API calls fail in development
        if (error.response?.status === 401) {
          const token = useAppStore.getState().token;
          const isDev = import.meta.env.DEV;
          const isMockToken = token === 'dev-token';
          
          // In production or if token is a real JWT, logout on 401
          // In dev mode with mock token, just log a warning
          if (!isDev || !isMockToken) {
            console.warn('[API] 401 Unauthorized - logging out');
            useAppStore.getState().logout();
          } else {
            console.warn('[API] 401 in dev mode with mock token - keeping user state');
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

export const api = new ApiClient();

// Auth API
export const authApi = {
  authenticate: (initData: string, referralCode?: string) => {
    // Get user's timezone for optimal reminder scheduling
    let timezone: string | undefined;
    let timezoneOffset: number | undefined;
    
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      timezoneOffset = new Date().getTimezoneOffset() * -1; // Convert to positive offset in minutes
    } catch {
      // Fallback if Intl API not available
      timezoneOffset = new Date().getTimezoneOffset() * -1;
    }
    
    return api.post<{
      token: string;
      user: {
        telegramId: string;
        username?: string;
        firstName?: string;
        balance: number;
        level: number;
        experience: number;
        streak: number;
        referralCode: string;
        isNewUser: boolean;
        isBanned?: boolean;
        banReason?: string;
      };
    }>('/auth/telegram', { initData, referralCode, timezone, timezoneOffset });
  },

  getProfile: () => api.get<{
    telegramId: string;
    username?: string;
    firstName?: string;
    balance: number;
    totalEarned?: number;
    level: number;
    experience: number;
    streak: { count: number };
    referral?: { code: string };
    kycStatus?: boolean;
    isBanned?: boolean;
    banReason?: string;
  }>('/auth/profile'),

  checkChannelMembership: () => api.get<{
    isMember: boolean;
    status?: string;
    error?: string;
  }>('/auth/check-channel'),

  updateWallet: (walletAddress: string) => api.put('/auth/wallet', { walletAddress }),
  submitFeedback: (rating: number, comment: string, type?: 'suggestion' | 'bug' | 'other') =>
    api.post<{ success: boolean; reward: number; newBalance?: number }>('/auth/feedback', { rating, comment, type }),

  // Notifications
  getNotifications: () => api.get<{
    notifications: Array<{
      id: string;
      type: 'refund' | 'info' | 'warning' | 'success';
      title: string;
      message: string;
      createdAt: string;
      read: boolean;
    }>;
    count: number;
  }>('/auth/notifications'),
  
  markNotificationsRead: (notificationIds?: string[]) =>
    api.post('/auth/notifications/read', { notificationIds }),

  // Bot status - check if user has started the Telegram bot
  getBotStatus: () => api.get<{
    telegram_id: number;
    bot_started: boolean;
    bot_started_at: string | null;
  }>('/auth/bot-status'),

  // Sync cached balance from localStorage to server (one-time migration)
  syncCachedBalance: (cachedBalance: number) => api.post<{
    success: boolean;
    synced: boolean;
    previousBalance?: number;
    newBalance?: number;
    serverBalance?: number;
    difference?: number;
    message?: string;
  }>('/auth/sync-balance', { cachedBalance }),
};

// Faucet API
export const faucetApi = {
  claim: (captchaToken?: string) =>
    api.post<{
      success: boolean;
      amount?: number;
      newBalance?: number;
      streak?: number;
      streakBonus?: number;
    }>('/faucet/claim', { captchaToken }),

  getStatus: () =>
    api.get<{
      canClaim: boolean;
      cooldownRemaining: number;
      streak: number;
      nextRewardRange: { min: number; max: number };
    }>('/faucet/status'),
};

// Auto Faucet API
export const autoFaucetApi = {
  start: () =>
    api.post<{ success: boolean; sessionToken: string; energy: number }>('/autofaucet/start'),
  stop: () => api.post('/autofaucet/stop'),
  heartbeat: (sessionToken: string) =>
    api.post<{ 
      success: boolean; 
      energy: number; 
      isActive: boolean; 
      newBalance?: number;
      reward?: number;
      cyclesCompleted?: number;
      message?: string;
    }>('/autofaucet/heartbeat', {
      sessionToken,
    }),
  // Complete cycle and auto-restart - called when session timer reaches 0
  completeCycle: (sessionToken: string) =>
    api.post<{
      success: boolean;
      isActive: boolean;
      energy: number;
      reward?: number;
      newBalance?: number;
      cyclesCompleted?: number;
      cyclesCompletedToday?: number;
      newSessionToken?: string;
      shouldRestart: boolean;
      canStartNewSession?: boolean;
      sessionDuration?: number;
      adsPerSession?: number;
      message?: string;
    }>('/autofaucet/complete-cycle', {
      sessionToken,
    }),
  getStatus: () =>
    api.get<{
      isActive: boolean;
      energy: number;
      maxEnergy: number;
      todayCycles: number;
      maxDailyCycles: number;
      rewardPerCycle: number;
      sessionDuration: number;
      adsPerSession: number;
      adInterval: number; // Show ad every N seconds
      earnPerCycle: number;
    }>('/autofaucet/status'),
  getEnergy: () => api.get<{ energy: number; maxEnergy: number }>('/autofaucet/energy'),
  startAdSession: (adNetwork?: string, adType?: string) =>
    api.post<{ success: boolean; sessionToken: string; minDuration: number; expiresIn: number }>(
      '/autofaucet/energy/start-session',
      { adNetwork: adNetwork || 'autofaucet_energy', adType: adType || 'rewarded' }
    ),
  earnEnergyFromAd: (sessionToken: string) =>
    api.post<{ success: boolean; energyAdded: number; newEnergy: number }>('/autofaucet/energy/earn', { sessionToken }),
  claimDailyFreeEnergy: () =>
    api.post<{ success: boolean; energyAdded: number; newEnergy: number; error?: string }>('/autofaucet/energy/daily-free'),
  purchaseEnergy: (packageId: string) =>
    api.post<{ success: boolean; energyAdded: number; newEnergy: number }>('/autofaucet/purchase', {
      packageId,
    }),
  // Energy packages
  getEnergyPackages: () =>
    api.get<{
      packages: Array<{
        _id: string;
        name: string;
        description?: string;
        price: number;
        energyGranted: number;
        badge?: string;
      }>;
    }>('/autofaucet/energy-packages'),
  purchaseEnergyPackage: (packageId: string) =>
    api.post<{
      success: boolean;
      energyAdded: number;
      newEnergy: number;
      newBalance: number;
      message: string;
    }>('/autofaucet/purchase-package', { packageId }),
  // Log ad view for analytics
  logAdView: (adType: 'session_start' | 'interval' | 'session_end' | 'energy_earn') =>
    api.post('/autofaucet/log-ad', { adType }),
};

// Ads API - AdExtra Integration
export const adsApi = {
  // Get available ads with placement IDs
  getAvailable: () =>
    api.get<{
      ads: Array<{
        slotId: string;
        placementId: string;
        type: string;
        available: boolean;
        reward: number;
        remainingToday: number;
        cooldownRemaining: number;
      }>;
    }>('/ads/available'),

  // Create ad session before showing ad (call this first)
  createSession: (adType: string) =>
    api.post<{
      success: boolean;
      sessionId?: string;
      placementId?: string;
      reward?: number;
      error?: string;
    }>('/ads/session', { adType }),

  // Verify ad completion (call after p_adextra onSuccess)
  verifyCompletion: (sessionId: string, placementId: string, adType: string) =>
    api.post<{
      success: boolean;
      reward?: number;
      newBalance?: number;
      error?: string;
    }>('/ads/verify', { sessionId, placementId, adType }),

  // Report ad error (call after p_adextra onError)
  reportError: (sessionId: string, errorDetails?: string) =>
    api.post<{ success: boolean }>('/ads/error', { sessionId, errorDetails }),

  // Start ad session - MUST be called BEFORE showing ad
  // Returns a session token that must be provided when claiming reward
  startAdSession: (adNetwork: string, adType: string) =>
    api.post<{
      success: boolean;
      sessionToken: string;
      minDuration: number;
      expiresIn: number;
    }>('/ads/start', { adNetwork, adType }),

  // Record ad view from third-party networks (Adsgram, Onclicka) - updates quest progress
  // NOTE: Requires valid sessionToken from startAdSession
  // NOTE: Reward amount is determined SERVER-SIDE ONLY for security
  recordAdView: (adNetwork: string, adType: string, sessionToken: string) =>
    api.post<{
      success: boolean;
      questUpdated?: boolean;
      newBalance?: number;
      reward?: number; // Server-calculated reward amount
    }>('/ads/record', { adNetwork, adType, sessionToken }),

  // Get ad cooldowns for current user
  getAdCooldowns: () =>
    api.get<{
      cooldowns: Array<{
        adType: string;
        network: string;
        remainingSeconds: number;
        totalSeconds: number;
      }>;
      globalCooldown: number;
      dailyLimits: Array<{
        network: string;
        used: number;
        limit: number;
        remaining: number;
      }>;
    }>('/ads/cooldowns'),

  // Legacy methods for PTC and surveys
  getPtcAds: () =>
    api.get<{
      ads: Array<{
        id: string;
        title: string;
        url: string;
        reward: number;
        duration: number;
        viewed: boolean;
      }>;
    }>('/ads/ptc'),
  getSurveys: () =>
    api.get<{
      surveys: Array<{
        id: string;
        title: string;
        description: string;
        reward: number;
        estimatedTime: number;
        url: string;
      }>;
    }>('/ads/surveys'),

  // Deprecated - use createSession + verifyCompletion instead
  getInfo: () => adsApi.getAvailable(),
  request: () => Promise.reject(new Error('Deprecated: use createSession instead')),
  verify: (_id: string) => Promise.reject(new Error('Deprecated: use verifyCompletion instead')),

  // GigaPub OfferWall reward validation
  validateGigaPubReward: (data: { 
    rewardId: string; 
    userId: string;
    projectId: string;
    hash: string; 
    amount: number;
    description?: string;
  }) =>
    api.post<{
      success: boolean;
      confirmationHash?: string;
      alreadyClaimed?: boolean;
      message?: string;
      error?: string;
    }>('/ads/gigapub/validate', data),
};


// Game API
export const gameApi = {
  spinWheel: () =>
    api.post<{
      success: boolean;
      reward?: number;
      prize?: number;
      rewardLabel?: string;
      rewardType?: 'points' | 'ton' | 'keys' | 'bonus' | 'multiplier' | 'nothing';
      segmentIndex?: number;
      segment?: {
        label: string;
        rewardType: string;
        rewardValue: number;
        color: string;
      };
      spinsRemaining?: number;
      keysRemaining?: number;
      newBalance?: number;
      spinsToday?: number;
      canSpinAgain?: boolean;
      cooldownRemaining?: number;
      error?: string;
    }>('/game/wheel/spin'),
  getWheelStatus: () =>
    api.get<{
      canSpin: boolean;
      cooldownRemaining: number;
      adCooldownRemaining: number;
      spinsToday: number;
      bonusSpins: number;
      spinKeys: number;
      isEnabled: boolean;
      disabledMessage?: string;
      rewards: Array<{ value: number; label: string }>;
      segments: Array<{
        _id?: string;
        label: string;
        rewardType: 'points' | 'ton' | 'keys' | 'bonus' | 'multiplier' | 'nothing';
        rewardValue: number;
        color: string;
        icon?: string;
        probability: number;
        order: number;
        isEnabled: boolean;
      }>;
      config: {
        spinDuration: number;
        spinRotations: number;
        soundEnabled: boolean;
        vibrationEnabled: boolean;
        keyRequired: boolean;
        keysPerSpin: number;
        adKeyEnabled: boolean;
        maxAdKeysPerDay: number;
      };
    }>('/game/wheel/status'),
  
  // Spin Keys System
  watchAdForKey: () =>
    api.post<{
      success: boolean;
      keysGranted?: number;
      spinKeys?: number;
      cooldownRemaining?: number;
      error?: string;
    }>('/game/wheel/watch-ad'),
  getKeyPackages: () =>
    api.get<{
      packages: Array<{
        _id: string;
        name: string;
        description?: string;
        price: number;
        keysGranted: number;
        badge?: string;
      }>;
    }>('/game/wheel/packages'),
  purchaseKeyPackage: (packageId: string) =>
    api.post<{
      success: boolean;
      keysGranted?: number;
      spinKeys?: number;
      newBalance?: number;
      error?: string;
    }>('/game/wheel/purchase', { packageId }),
  
  getQuests: () =>
    api.get<{
      quests: Array<{
        id: string;
        type: string;
        title: string;
        description: string;
        target: number;
        current: number;
        progress: number;
        reward: number;
        completed: boolean;
        claimed: boolean;
      }>;
    }>('/game/quests'),
  claimQuest: (questId: string) =>
    api.post<{ success: boolean; reward: number; newBalance: number }>(`/game/quests/${questId}/claim`),
  getAchievements: () =>
    api.get<{
      achievements: Array<{
        id: string;
        title: string;
        description: string;
        badge: string;
        reward: number;
        unlocked: boolean;
        claimed: boolean;
        progress: number;
        requirement: number;
      }>;
    }>('/game/achievements'),
  claimAchievement: (achievementId: string) =>
    api.post<{ success: boolean; reward: number; newBalance?: number }>(`/game/achievements/${achievementId}/claim`),
};

// Referral API
export const referralApi = {
  getCode: () =>
    api.get<{ code: string; shareUrl: string; shareText: string }>('/referral/code'),
  getStats: () =>
    api.get<{
      code: string;
      referralCode: string;
      referralLink: string;
      totalReferrals: number;
      level1: { count: number; percentage: number };
      level2: { count: number; percentage: number };
      level3: { count: number; percentage: number };
      tiers: Array<{ level: number; count: number; percentage: number }>;
      totalEarnings: number;
      recentReferrals: Array<{ username: string; joinedAt: Date; earnings: number }>;
    }>('/referral/stats'),
  getLeaderboard: (type?: string) =>
    api.get<{
      leaderboard: Array<{ rank: number; username: string; value: number; isCurrentUser: boolean }>;
      entries: Array<{ rank: number; username: string; value: number; isCurrentUser: boolean }>;
      userRank: number;
      userValue: number;
    }>('/referral/leaderboard', { params: { type } }),
};

// Withdraw API
export const withdrawApi = {
  request: (pointsAmount: number, captchaToken?: string) =>
    api.post<{
      success: boolean;
      withdrawalId?: string;
      tonAmount?: number;
      netAmount?: number;
      fee?: number;
      newBalance?: number;
      requiresKyc?: boolean;
    }>('/withdraw/request', { pointsAmount, captchaToken }),
  requestWithdrawal: (pointsAmount: number, captchaToken?: string) =>
    api.post<{
      success: boolean;
      withdrawalId?: string;
      tonAmount?: number;
      netAmount?: number;
      fee?: number;
      newBalance?: number;
      requiresKyc?: boolean;
    }>('/withdraw/request', { pointsAmount, captchaToken }),
  getHistory: (page?: number, limit?: number) =>
    api.get<{
      withdrawals: Array<{
        id: string;
        amount: number;
        tonAmount: number;
        netAmount: number;
        fee: number;
        status: 'pending' | 'processing' | 'completed' | 'failed' | 'approved' | 'rejected';
        withdrawalType: 'auto' | 'manual';
        paymentMethodName?: string;
        walletAddress?: string;
        transactionHash?: string;
        adminNotes?: string;
        createdAt: Date;
      }>;
      total: number;
      pages: number;
    }>('/withdraw/history', { params: { page, limit } }),
  getStatus: (id: string) =>
    api.get<{ status: string; transactionHash?: string; explorerLink?: string }>(
      `/withdraw/status/${id}`
    ),
  getSettings: () =>
    api.get<{
      minTonAmount: number;
      platformFeePercent: number;
      kycThreshold: number;
      pointsToTonRate: number;
      currentBalance: number;
      walletAddress?: string;
      kycVerified: boolean;
      estimatedTon: number;
    }>('/withdraw/info'),
  getInfo: () =>
    api.get<{
      minTonAmount: number;
      platformFeePercent: number;
      kycThreshold: number;
      pointsToTonRate: number;
      currentBalance: number;
      walletAddress?: string;
      kycVerified: boolean;
      estimatedTon: number;
    }>('/withdraw/info'),
  getEnergyPackages: () =>
    api.get<{
      packages: Array<{ id: string; energy: number; tonPrice: number; label: string }>;
    }>('/withdraw/energy-packages'),
};

// Payment Methods API
export interface PaymentMethodField {
  name: string;
  label: string;
  type: 'text' | 'tel' | 'email' | 'number' | 'select';
  placeholder?: string;
  validation?: string;
  required: boolean;
  options?: Array<{ value: string; label: string }>;
}

export interface PaymentMethod {
  _id: string;
  name: string;
  slug: string;
  logo?: string;
  description?: string;
  minWithdrawal: number;
  maxWithdrawal: number;
  conversionRate: number;
  processingTime: string;
  fee: number;
  feeType: 'fixed' | 'percentage';
  requiredFields: PaymentMethodField[];
  instructions?: string;
  isEnabled: boolean;
  sortOrder: number;
}

export const paymentApi = {
  // Get enabled payment methods (for clients)
  getMethods: () =>
    api.get<{
      success: boolean;
      methods: PaymentMethod[];
    }>('/payment/methods'),

  // Request manual withdrawal
  requestManualWithdrawal: (
    pointsAmount: number,
    paymentMethodId: string,
    paymentDetails: Record<string, string>,
    calculatedValues?: {
      localCurrencyAmount: number;
      localCurrency: string;
    }
  ) =>
    api.post<{
      success: boolean;
      withdrawalId?: string;
      tonAmount?: number;
      netAmount?: number;
      fee?: number;
      newBalance?: number;
      paymentMethodName?: string;
      message?: string;
      error?: string;
    }>('/payment/manual/request', { 
      pointsAmount, 
      paymentMethodId, 
      paymentDetails,
      ...calculatedValues
    }),

  // Cancel manual withdrawal
  cancelWithdrawal: (withdrawalId: string) =>
    api.post<{
      success: boolean;
      message?: string;
      error?: string;
    }>(`/payment/manual/${withdrawalId}/cancel`),
};

// Redeem Code API
export interface RedemptionHistoryItem {
  code: string;
  amountRewarded: number;
  redeemedAt: string;
}

export const redeemApi = {
  // Redeem a code
  redeem: (code: string) =>
    api.post<{
      success: boolean;
      amountRewarded?: number;
      newBalance?: number;
      message?: string;
      error?: string;
      errorCode?: string;
    }>('/redeem', { code }),

  // Get user's redemption history
  getHistory: () =>
    api.get<{
      success: boolean;
      redemptions: RedemptionHistoryItem[];
    }>('/redeem/history'),
};

// ==================== SUPPORT TICKET API ====================

export interface TicketAttachment {
  _id?: string;
  filename: string;
  mimetype: string;
  url: string; // Cloudinary URL
  publicId?: string; // Cloudinary public ID
  size: number;
  width?: number;
  height?: number;
  uploadedAt: string;
}

export interface TicketReply {
  _id: string;
  message: string;
  timestamp: string;
  isAdmin: boolean;
  adminName?: string;
  attachments?: TicketAttachment[];
}

export interface Ticket {
  _id: string;
  ticketId: string;
  telegramId: string;
  username?: string;
  firstName?: string;
  email?: string;
  subject: string;
  description: string;
  status: 'open' | 'pending' | 'in-progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category?: string;
  attachments?: TicketAttachment[];
  replies: TicketReply[];
  assignedTo?: string;
  resolvedAt?: string;
  closedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TicketListResponse {
  data: Ticket[];
  total: number;
  page: number;
  pages: number;
}

export const ticketApi = {
  // Create a new support ticket (with optional file attachments)
  createTicket: (data: {
    subject: string;
    description: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    category?: string;
    email?: string;
    attachments?: File[];
  }) => {
    const formData = new FormData();
    formData.append('subject', data.subject);
    formData.append('description', data.description);
    if (data.priority) formData.append('priority', data.priority);
    if (data.category) formData.append('category', data.category);
    if (data.email) formData.append('email', data.email);
    
    // Append files if any
    if (data.attachments) {
      data.attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }
    
    return api.post<{
      success: boolean;
      ticket: Ticket;
      error?: string;
    }>('/tickets', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Get user's tickets
  getMyTickets: (page: number = 1, limit: number = 10) =>
    api.get<TicketListResponse>(`/tickets/my?page=${page}&limit=${limit}`),

  // Get a specific ticket
  getTicket: (ticketId: string) =>
    api.get<{
      ticket: Ticket;
    }>(`/tickets/${ticketId}`),

  // Add reply to ticket (with optional file attachments)
  addReply: (ticketId: string, message: string, attachments?: File[]) => {
    const formData = new FormData();
    formData.append('message', message);
    
    // Append files if any
    if (attachments) {
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }
    
    return api.post<{
      success: boolean;
      ticket: Ticket;
      error?: string;
    }>(`/tickets/${ticketId}/reply`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// FaucetPay API
export interface FaucetPayCoinConfig {
  coin: string;
  enabled: boolean;
  minWithdrawal: number;
  maxWithdrawal?: number;
  feePercent: number;
}

export interface FaucetPayPublicConfig {
  enabled: boolean;
  emergencyStop: boolean;
  coins: FaucetPayCoinConfig[];
  processingTime: string;
  description?: string;
  maxDailyWithdrawals?: number;
  cooldownMinutes?: number;
}

export interface FaucetPayWithdrawalRequest {
  faucetPayEmail: string;
  coin: string;
  pointsAmount: number;
}

export interface FaucetPayWithdrawalResponse {
  success: boolean;
  message: string;
  withdrawalId: string;
  netAmount?: number;
  newBalance?: number;
  error?: string;
}

export interface FaucetPayWithdrawalHistory {
  _id: string;
  coin: string;
  pointsAmount: number;
  coinAmount: number;
  netAmount: number;
  feeAmount: number;
  faucetPayEmail: string;
  faucetPayPayoutId?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
  errorMessage?: string;
  createdAt: string;
  completedAt?: string;
}

export interface FaucetPayCooldownInfo {
  canWithdraw: boolean;
  cooldownRemaining?: number;
  dailyLimitRemaining?: number;
  dailyLimitUsed?: number;
}

export interface FaucetPayConversionPreview {
  coin: string;
  pointsAmount: number;
  // Conversion rates used (from admin withdrawal settings)
  pointsToTonRate: number;
  tonUsdRate: number;
  // Calculated values
  usdEquivalent: number;
  coinAmount: number;
  coinRate: number;
  priceSource: string;
  feePercent: number;
  feeAmount: number;
  netAmount: number;
}

export const faucetPayApi = {
  // Get public FaucetPay configuration
  getConfig: () =>
    api.get<FaucetPayPublicConfig>('/faucetpay/config'),

  // Request withdrawal to FaucetPay
  requestWithdrawal: (data: FaucetPayWithdrawalRequest) =>
    api.post<FaucetPayWithdrawalResponse>('/faucetpay/withdraw', data),

  // Get user's FaucetPay withdrawal history
  getHistory: (page: number = 1, limit: number = 10) =>
    api.get<{
      withdrawals: FaucetPayWithdrawalHistory[];
      total: number;
      page: number;
      pages: number;
    }>(`/faucetpay/history?page=${page}&limit=${limit}`),

  // Get status of a specific withdrawal
  getStatus: (withdrawalId: string) =>
    api.get<{
      withdrawal: FaucetPayWithdrawalHistory;
    }>(`/faucetpay/status/${withdrawalId}`),

  // Check cooldown and daily limits
  checkCooldown: () =>
    api.get<FaucetPayCooldownInfo>('/faucetpay/cooldown'),

  // Preview conversion from points to coin
  previewConversion: (coin: string, points: number) =>
    api.get<FaucetPayConversionPreview>(`/faucetpay/preview?coin=${coin}&points=${points}`),
};

// ============================================
// TASKS API (Promotional/Advertising System)
// ============================================

export interface Task {
  id: string;
  type: 'telegram_bot' | 'telegram_channel' | 'telegram_group' | 'telegram_miniapp' | 'social_like' | 'social_share' | 'social_visit';
  title: string;
  description: string;
  thumbnailUrl?: string;
  reward: number;
  estimatedTime: number;
  remaining: number;
  totalTarget: number;
  advertiser?: string;
  telegramUsername?: string;
  targetUrl?: string;
  miniAppUrl?: string;
  isBoosted: boolean;
}

export interface TaskStartResponse {
  success: boolean;
  message?: string;
  task?: {
    id: string;
    taskCompletionId: string;
    status: string;
    startedAt: string;
    expiresAt: string;
    reward: number;
    countdown: number;
    targetUrl: string;
  };
  ad?: {
    id: string;
    type: string;
    title: string;
    description: string;
    verificationTime: number;
    actionLink: string;
  };
  error?: string;
  cooldownUntil?: number;
}

export interface TaskVerifyResponse {
  success: boolean;
  message?: string;
  status?: string;
  reward?: number;
  newBalance?: number;
  attemptsLeft?: number;
  cooldownUntil?: number;
  error?: string;
  retryAfter?: number;
}

export interface TaskStats {
  todayCompleted: number;
  remainingToday: number;
  maxDailyTasks: number;
  totalEarnings: number;
  cooldownSeconds: number;
}

export interface TaskHistoryItem {
  id: string;
  status: string;
  reward: number;
  startedAt: string;
  completedAt?: string;
  ad: {
    type: string;
    title: string;
    thumbnailUrl?: string;
  } | null;
}

export const tasksApi = {
  // Get available tasks
  getTasks: (type?: string, page = 1, limit = 20) =>
    api.get<{
      success: boolean;
      tasks: Task[];
      todayCompleted: number;
      remainingToday: number;
      maxDailyTasks: number;
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/tasks?${type ? `type=${type}&` : ''}page=${page}&limit=${limit}`),

  // Start a task
  startTask: (adId: string) =>
    api.post<TaskStartResponse>(`/tasks/${adId}/start`),

  // Verify and complete a task
  verifyTask: (taskId: string) =>
    api.post<TaskVerifyResponse>(`/tasks/${taskId}/verify`),

  // Get task status
  getTaskStatus: (taskId: string) =>
    api.get<{
      success: boolean;
      task: {
        id: string;
        status: string;
        startedAt: string;
        expiresAt: string;
        reward: number;
        elapsedSeconds: number;
        remainingSeconds: number;
        canVerify: boolean;
        ad: {
          type: string;
          title: string;
          verificationTime: number;
        } | null;
      };
    }>(`/tasks/${taskId}/status`),

  // Get task stats
  getStats: () =>
    api.get<{ success: boolean; stats: TaskStats }>('/tasks/stats'),

  // Get task history
  getHistory: (status?: string, page = 1, limit = 20) =>
    api.get<{
      success: boolean;
      tasks: TaskHistoryItem[];
      totalEarnings: number;
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/tasks/history?${status ? `status=${status}&` : ''}page=${page}&limit=${limit}`),
};

// ============================================
// ADVERTISER API
// ============================================
// Note: All users can be advertisers. The advertiser profile is linked to the user
// via telegramId (same identity, no separate advertiser ID needed for users).

export interface AdvertiserProfile {
  id: string;
  telegramId: string;  // Same as user's telegramId - this is the universal identifier
  userId: string;      // Reference to the user document
  businessName?: string;
  website?: string;
  status: 'active' | 'suspended' | 'banned';
  kycVerified: boolean;
  wallet: {
    balance: number;
    lockedBalance: number;
    totalBalance: number;
    totalDeposited: number;
    totalSpent: number;
  };
  stats: {
    totalAds: number;
    activeAds: number;
    pendingAds: number;
    completedAds: number;
    totalImpressions: number;
    totalClicks: number;
    totalCompletions: number;
    totalSpent: number;
  };
  createdAt: string;
}

export interface AdConfig {
  platformFeePercent: number;
  minRewardPerTask: number;
  maxRewardPerTask: number;
  minAdBudget: number;
  minAdvertiserDeposit: number;
  maxActiveAdsPerAdvertiser: number;
  supportedCryptos: string[];
  adsEnabled: boolean;
  newAdsEnabled: boolean;
}

export interface AdDetails {
  id: string;
  type: string;
  status: string;
  title: string;
  description: string;
  thumbnailUrl?: string;
  rewardPerCompletion: number;
  targetCompletions: number;
  completedCount: number;
  verificationTime: number;
  budgetRemaining: number;
  stats: {
    impressions: number;
    clicks: number;
    completions: number;
    failures: number;
    spent: number;
  };
  createdAt: string;
}

export interface CreateAdRequest {
  type: string;
  title: string;
  description: string;
  thumbnailUrl?: string;
  telegramUsername?: string;
  telegramChatId?: string;
  botDeepLink?: string;
  miniAppUrl?: string;
  targetUrl?: string;
  socialPlatform?: string;
  socialAction?: string;
  rewardPerCompletion: number;
  targetCompletions: number;
  dailyLimit?: number;
  startDate?: string;
  endDate?: string;
  countries?: string[];
  languages?: string[];
  minLevel?: number;
}

export interface CostCalculation {
  rewardPerCompletion: number;
  platformFeePerCompletion: number;
  platformFeePercent: number;
  totalCostPerCompletion: number;
  targetCompletions: number;
  totalBudget: number;
  minBudget: number;
  isValid: boolean;
}

export const advertiserApi = {
  // Get advertiser profile
  getProfile: () =>
    api.get<{ success: boolean; advertiser: AdvertiserProfile; message?: string }>('/advertiser/profile'),

  // Register as advertiser
  register: () =>
    api.post<{ success: boolean; profile?: AdvertiserProfile; message?: string }>('/advertiser/register'),

  // Update profile
  updateProfile: (data: { businessName?: string; website?: string }) =>
    api.put<{ success: boolean; advertiser: Partial<AdvertiserProfile> }>('/advertiser/profile', data),

  // Get deposit configuration (available methods)
  getDepositConfig: () =>
    api.get<{ 
      success: boolean; 
      methods: Array<{
        id: string;
        name: string;
        description: string;
        available: boolean;
        networks?: string[];
        exchangeRates?: Record<string, number>;
        currencies?: string[];
        pointsPerUsd?: number;
      }>;
    }>('/advertiser/deposit-config'),

  // Deposit funds
  deposit: (data: { amount: number; method?: string; network?: string }) =>
    api.post<{ 
      success: boolean; 
      deposit?: {
        id: string;
        amount: number;
        originalAmount: number;
        originalCurrency: string;
        walletAddress: string;
        network: string;
        status: string;
        expiresAt: string;
      };
      faucetPay?: {
        paymentUrl: string;
      };
      error?: string;
    }>('/advertiser/deposit', data),

  // Get wallet and transactions
  getWallet: (page = 1, limit = 20) =>
    api.get<{
      success: boolean;
      wallet: AdvertiserProfile['wallet'];
      transactions: Array<{
        id: string;
        type: string;
        amount: number;
        description: string;
        createdAt: string;
      }>;
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/advertiser/wallet?page=${page}&limit=${limit}`),

  // Get deposit history
  getDeposits: (page = 1, limit = 20) =>
    api.get<{
      success: boolean;
      deposits: Array<{
        id: string;
        amount: number;
        method: string;
        network?: string;
        status: string;
        transactionHash?: string;
        createdAt: string;
      }>;
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/advertiser/deposits?page=${page}&limit=${limit}`),

  // Get transaction history (alias for deposits)
  getTransactions: (page = 1, limit = 20) =>
    api.get<{
      success: boolean;
      deposits: Array<{
        id: string;
        amount: number;
        method: string;
        network?: string;
        status: string;
        transactionHash?: string;
        createdAt: string;
      }>;
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/advertiser/deposits?page=${page}&limit=${limit}`),

  // Get ad system config
  getConfig: () =>
    api.get<{ success: boolean; config: AdConfig }>('/advertiser/config'),

  // Calculate ad cost
  calculateCost: (rewardPerCompletion: number, targetCompletions: number) =>
    api.post<{ success: boolean; calculation: CostCalculation }>('/advertiser/calculate-cost', {
      rewardPerCompletion,
      targetCompletions,
    }),

  // Get advertiser analytics
  getAnalytics: (period?: '7d' | '30d' | 'all') =>
    api.get<{
      success: boolean;
      analytics: {
        totalAds: number;
        activeAds: number;
        totalSpent: number;
        totalImpressions: number;
        totalCompletions: number;
        averageCompletionRate: number;
        dailyStats: Array<{ date: string; impressions: number; completions: number; spent: number }>;
      };
    }>(`/advertiser/analytics${period ? `?period=${period}` : ''}`),

  // Create new ad
  createAd: (data: CreateAdRequest) =>
    api.post<{
      success: boolean;
      ad?: AdDetails;
      message?: string;
      error?: string;
    }>('/advertiser/ads', data),

  // Get my ads
  getAds: (status?: string, page = 1, limit = 20) =>
    api.get<{
      success: boolean;
      ads: AdDetails[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/advertiser/ads?${status ? `status=${status}&` : ''}page=${page}&limit=${limit}`),

  // Get ad details
  getAdDetails: (adId: string) =>
    api.get<{
      success: boolean;
      ad: AdDetails & {
        details: {
          telegramUsername?: string;
          targetUrl?: string;
          miniAppUrl?: string;
        };
        targeting: {
          countries?: string[];
          languages?: string[];
          minLevel?: number;
        };
      };
      analytics: {
        dailyStats: Array<{ date: string; impressions: number; completions: number; spent: number }>;
        completionsByHour: Array<{ hour: number; count: number }>;
      };
    }>(`/advertiser/ads/${adId}`),

  // Pause ad
  pauseAd: (adId: string) =>
    api.post<{ success: boolean; ad: { id: string; status: string }; message?: string }>(`/advertiser/ads/${adId}/pause`),

  // Resume ad
  resumeAd: (adId: string) =>
    api.post<{ success: boolean; ad: { id: string; status: string }; message?: string }>(`/advertiser/ads/${adId}/resume`),

  // Cancel ad
  cancelAd: (adId: string) =>
    api.post<{ success: boolean; ad: { id: string; status: string }; message?: string }>(`/advertiser/ads/${adId}/cancel`),
};
